import axios from "axios";
import { getToken } from "./userService";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1";

/**
 * Submit feedback for a game
 * @param {Object} feedbackData - Feedback data object
 * @param {number} feedbackData.gameId - Game ID (required)
 * @param {number} feedbackData.assignmentId - Assignment ID (optional)
 * @param {string} feedbackData.comments - General comments (optional)
 * @param {Array} feedbackData.bugReports - Array of bug reports (optional)
 * @param {Array} feedbackData.suggestions - Array of suggestions (optional)
 * @returns {Promise<Object>} Response with success status and feedbackId
 */
export async function submitFeedback(feedbackData) {
  try {
    // Build the payload - ONLY include fields that have actual values
    // Do NOT send null or empty arrays - let backend use database defaults
    const payload = {
      gameId: feedbackData.gameId,
    };

    // Only add assignmentId if it exists
    if (feedbackData.assignmentId) {
      payload.assignmentId = feedbackData.assignmentId;
    }

    // Only add comments if it has content
    if (feedbackData.comments && feedbackData.comments.trim().length > 0) {
      payload.comments = feedbackData.comments.trim();
    }

    // Only add bugReports if it's a non-empty array
    if (feedbackData.bugReports && Array.isArray(feedbackData.bugReports) && feedbackData.bugReports.length > 0) {
      payload.bugReports = feedbackData.bugReports;
    }

    // Only add suggestions if it's a non-empty array
    if (feedbackData.suggestions && Array.isArray(feedbackData.suggestions) && feedbackData.suggestions.length > 0) {
      payload.suggestions = feedbackData.suggestions;
    }

    const response = await axios.post(
      `${API_BASE_URL}/feedback/submit`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    throw error;
  }
}

/**
 * Check if user has already submitted feedback for a game
 * @param {number} gameId - Game ID
 * @returns {Promise<Object>} Response with hasFeedback boolean
 */
export async function checkFeedbackStatus(gameId) {
  try {
    if (!gameId) {
      return { hasFeedback: false };
    }

    const response = await axios.get(
      `${API_BASE_URL}/feedback/check/${gameId}`,
      {
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    // If endpoint doesn't exist (404) or has issues (400), assume no feedback exists
    // This allows the form to be shown even if the check endpoint isn't working
    if (error.response) {
      const status = error.response.status;
      if (status === 400 || status === 404) {
        return { hasFeedback: false };
      }
    }
    // Return false to allow form to be shown
    return { hasFeedback: false };
  }
}

/**
 * Get user's feedback history
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of results to return (default: 10)
 * @param {number} options.offset - Offset for pagination (default: 0)
 * @returns {Promise<Object>} Response with feedbacks array and total count
 */
export async function getMyFeedback({ limit = 10, offset = 0 } = {}) {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/feedback/my-feedback`,
      {
        params: {
          limit,
          offset,
        },
        headers: {
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    throw error;
  }
}

